<script setup lang="ts">
import { useCommonStore } from '@/stores/modules/common'
import { useUserStore } from '@/stores/modules/user'
import { useRoomStore } from '@/stores/modules/room'
import type { NestMonsterType } from '@/types/monster'
import type { RoomInfoType, RoomType } from '@/types/room'
import { formatNumberWithCommas } from '@/utils'
import { copyText } from '@/utils/copy-text'
import { socketEmitAsync, socketOn } from '@/utils/web-socket'
import { ref, watch } from 'vue'
import { permissionOverlays } from '@/utils/plus'
import { dayjs } from 'element-plus'
import { computed } from 'vue'
import RuneRuinMonsterDetail from '@/components/rune-ruin-monster-detail/RuneRuinMonsterDetail.vue'
import { soulClass } from '@/views/awaken/data'
import { crackRank, runeRuinRank } from '../data'
import { RuneRoleText } from '@/views/item/data'
import RelicRuinMonsterDetail from '@/components/relic-ruin-monster-detail/RelicRuinMonsterDetail.vue'
import { explainRelicIdList } from '@/core/relic'
import { getSkillNameById } from '@/core/skill'
import type { OtherUserInfoResType, OtherUserInfoType } from '@/types/fight'
import OtherUserInfo from '@/views/fight/components/OtherUserInfo.vue'

const roomTypeNameMap: Record<string, { name: string; classes: string }> = {
  nest: {
    name: '魔物巢穴',
    classes: 'pet',
  },
  plain: {
    name: '魔物旷野',
    classes: 'pet',
  },
  runeRuin: {
    name: '符石遗迹',
    classes: 'awaken',
  },
  crack: {
    name: '虚空裂隙',
    classes: 'peerless',
  },
  relicRuin: {
    name: '圣物遗迹',
    classes: 'relic',
  },
  skillCorridor: {
    name: '秘技回廊',
    classes: 'mp',
  },
  drageonMountain: {
    name: '魔龙山巅',
    classes: 'hp',
  },
}

const commonStore = useCommonStore()
const userStore = useUserStore()
const roomStore = useRoomStore()

const props = defineProps<{
  roomShow: boolean
  nestMonster?: NestMonsterType
  plainMonster?: NestMonsterType
  runeRuinMonster?: NestMonsterType
  crackMonster?: NestMonsterType
  relicRuinMonster?: NestMonsterType
  skillCorridorMonster?: NestMonsterType
  drageonMountainMonster?: NestMonsterType
}>()
const emits = defineEmits(['start', 'join', 'quit', 'clear', 'back', 'show', 'startFight'])

const curTab = ref('')

const monster = ref<NestMonsterType>()

const roomList = ref<RoomInfoType[]>()
const refreshTime = ref(0)
async function getRoomList() {
  const res = await socketEmitAsync<RoomInfoType[]>({ type: 'getTeamFightRoom', data: { type: curTab.value } })
  roomList.value = res.filter((item) => item.monster)
  ElMessage.success({
    message: '刷新成功',
    duration: 500,
  })
  refreshTime.value = 1
  const interval = setInterval(() => {
    refreshTime.value--
    if (refreshTime.value <= 0) {
      refreshTime.value = 0
      clearInterval(interval)
    }
  }, 1000)
}
async function getPersonRoomInfo() {
  const res = await socketEmitAsync<RoomInfoType>({ type: 'getPersonRoom' })
  if (!res) {
    roomStore.clearRoomInfo()
    monster.value = undefined
    return
  }
  const { id, name, creatorId, type } = res
  roomStore.id = id
  roomStore.name = name
  monster.value = res.monster
  if (creatorId === userStore.id) roomStore.autoCloseRoom()
  if (res.partnerInfo) {
    roomStore.partnerInfo = res.partnerInfo
    roomStore.isJoin = false
  } else if (res.creatorInfo) {
    roomStore.partnerInfo = res.creatorInfo
    roomStore.isJoin = true
  }
  roomStore.type = type
  emits('show')
}

async function handleCreateRoom() {
  switch (curTab.value) {
    case 'nest': {
      if (userStore.checkPetCapacity) return ElMessage.warning('魔物牧场已满，无法创建')
      if (!props.nestMonster) return ElMessage.warning('您暂未探寻到任何魔物，无法创建')
      await roomStore.createRoom('nest')
      monster.value = props.nestMonster
      break
    }
    case 'plain': {
      if (userStore.soulType <= 3) return ElMessage.warning('需完成二觉')
      if (userStore.checkPetCapacity) return ElMessage.warning('魔物牧场已满，无法创建')
      if (!props.plainMonster) return ElMessage.warning('您暂未探寻到任何魔物，无法创建')
      await roomStore.createRoom('plain')
      monster.value = props.plainMonster
      break
    }
    case 'runeRuin': {
      if (userStore.checkRuneCapacity) return ElMessage.warning('符石背包已满，无法创建')
      if (!props.runeRuinMonster) return ElMessage.warning('您暂未探索遗迹，无法创建')
      await roomStore.createRoom('runeRuin')
      monster.value = props.runeRuinMonster
      break
    }
    case 'crack': {
      if (!props.crackMonster) return ElMessage.warning('您暂未探索裂隙，无法创建')
      await roomStore.createRoom('crack')
      monster.value = props.crackMonster
      break
    }
    case 'relicRuin': {
      if (!props.relicRuinMonster) return ElMessage.warning('您暂未探索遗迹，无法创建')
      await roomStore.createRoom('relicRuin')
      monster.value = props.relicRuinMonster
      break
    }
    case 'skillCorridor': {
      if (userStore.soulType <= 3) return ElMessage.warning('需完成二觉')
      if (userStore.checkSkillCapacity) return ElMessage.warning('技能背包已满，无法创建')
      if (!props.skillCorridorMonster) return ElMessage.warning('您暂未探索回廊，无法创建')
      await roomStore.createRoom('skillCorridor')
      monster.value = props.skillCorridorMonster
      break
    }
    case 'drageonMountain': {
      if (!props.drageonMountainMonster) return ElMessage.warning('未获取魔龙山巅信息，无法创建')
      if (userStore.drageonMountainTimes <= 0 && userStore.drageonMountainRewardTimes > 0) return ElMessage.warning('您的挑战次数不足')
      const rank = props.drageonMountainMonster.rank || 1
      await roomStore.createRoom('drageonMountain', { rank })
      monster.value = props.drageonMountainMonster
      break
    }
    default: {
      ElMessage.warning('未选择组队类型，无法创建')
    }
  }
  roomStore.isJoin = false
}
async function handleJoinRoom(id: number, type: RoomType) {
  if (quitCoolDown.value) return ElMessage.warning('退房冷却中，请耐心等待10秒')
  switch (type) {
    case 'nest': {
      if (userStore.dailyChallengeLevel.diamond < 3) return ElMessage.warning('需通过钻石挑战 3级')
      break
    }
    case 'plain': {
      if (userStore.soulType <= 3) return ElMessage.warning('需完成二觉')
      break
    }
    case 'runeRuin':
    case 'crack': {
      if (!userStore.soulType) return ElMessage.warning('需觉醒')
      break
    }
    case 'relicRuin': {
      if (userStore.advanceLevel !== 4) return ElMessage.warning('需进阶')
      break
    }
    case 'skillCorridor': {
      if (userStore.soulType <= 3) return ElMessage.warning('需完成二觉')
      if (userStore.checkSkillCapacity) return ElMessage.warning('技能背包已满，无法加入')
      break
    }
    case 'drageonMountain': {
      if (userStore.endlessTowerLevel < 20) return ElMessage.warning('需通过无尽之塔 20层')
      if (userStore.drageonMountainTimes <= 0 && userStore.drageonMountainRewardTimes > 0) return ElMessage.warning('您的挑战次数不足')
      break
    }
  }
  const monsterInfo = await roomStore.joinRoom(id)
  monster.value = { ...monsterInfo }
}
const quitCoolDown = ref(0)
async function handleQuitRoom() {
  await roomStore.quitRoom()
  quitCoolDown.value = 10
  const interval = setInterval(() => {
    quitCoolDown.value--
    if (quitCoolDown.value <= 0) clearInterval(interval)
  }, 1000)
}
function handleJoinRoomById() {
  roomStore.joinRoomById(monster)
}
async function handleStartFight() {
  switch (roomStore.type) {
    case 'nest': {
      const res = await socketEmitAsync<NestMonsterType>({ type: 'startNestFight' })
      emits('start', { ...res, type: roomStore.type })
      break
    }
    case 'runeRuin': {
      const res = await socketEmitAsync<NestMonsterType>({ type: 'startRuneRuinFight' })
      emits('start', { ...res, type: roomStore.type })
      break
    }
    case 'crack': {
      const res = await socketEmitAsync<NestMonsterType>({ type: 'startCrackFight' })
      emits('start', { ...res, type: roomStore.type })
      break
    }
    case 'relicRuin': {
      const res = await socketEmitAsync<NestMonsterType>({ type: 'startRelicRuinFight' })
      emits('start', { ...res, type: roomStore.type })
      break
    }
    case 'skillCorridor': {
      const res = await socketEmitAsync<NestMonsterType>({ type: 'startSkillCorridorFight' })
      emits('start', { ...res, type: roomStore.type })
      break
    }
    case 'drageonMountain': {
      const res = await socketEmitAsync<NestMonsterType>({ type: 'startDrageonMountainFight' })
      emits('start', { ...res, type: roomStore.type })
      break
    }
    case 'plain': {
      const res = await socketEmitAsync<NestMonsterType>({ type: 'startPlainFight' })
      emits('start', { ...res, type: roomStore.type })
      break
    }
  }
  roomStore.closeTime = ''
  roomStore.clearRoomInfo()
}

const autoCloseTime = computed(() => Date.now() + dayjs(roomStore.closeTime).diff(dayjs(), 'ms'))

const showOtherUserInfo = ref(false)
const otherUserInfo = ref<OtherUserInfoType>()
function getSkillNameText(skillIdList: number[] = []) {
  return skillIdList.map((item) => getSkillNameById(item, true)).join('、')
}
async function handleShowOtherUserInfo() {
  const partnerInfo = roomStore.partnerInfo
  if (!partnerInfo) return
  const res = await socketEmitAsync<OtherUserInfoResType>({ type: 'seeUserInfo', data: { userId: partnerInfo.id } })
  otherUserInfo.value = {
    nickname: partnerInfo.nickname,
    nicknameColor: partnerInfo.nicknameColor,
    ...res,
  }
  showOtherUserInfo.value = true
}

watch(
  () => commonStore.isSocketInit,
  async (val) => {
    if (!val) return

    socketOn({
      type: 'teamFightStart',
      callBack: (res: NestMonsterType) => {
        roomStore.clearRoomInfo()
        emits('start', res)
      },
    })
  },
  { immediate: true },
)

watch(
  () => roomStore.partnerInfo,
  (val, oldVal) => {
    if (val && !oldVal && roomStore.isAutoStart && !roomStore.isJoin) {
      handleStartFight()
    }
  },
)
watch(
  () => props.roomShow,
  (val) => {
    if (!val) return

    getRoomList()
    getPersonRoomInfo()
    permissionOverlays()
  },
)

watch(
  () => commonStore.isSocketInit,
  async (val) => {
    if (!val) return
    getPersonRoomInfo()
  },
)

watch(
  () => commonStore.isSocketReconnect,
  async (val) => {
    if (!val) return

    getPersonRoomInfo()
  },
)

defineExpose({
  curTab,
})
</script>

<template>
  <div class="team-fight-room page">
    <h2>组队房间</h2>
    <el-tabs v-model="curTab" stretch @tab-change="getRoomList" size="small">
      <el-tab-pane label="全部" name="" class="pane"></el-tab-pane>
      <el-tab-pane label="魔物" name="nest" class="pane"></el-tab-pane>
      <el-tab-pane label="符石" name="runeRuin" class="pane"></el-tab-pane>
      <el-tab-pane label="虚空" name="crack" class="pane"></el-tab-pane>
      <el-tab-pane label="圣物" name="relicRuin" class="pane"></el-tab-pane>
      <el-tab-pane label="秘技" name="skillCorridor" class="pane"></el-tab-pane>
      <el-tab-pane label="魔龙" name="drageonMountain" class="pane"></el-tab-pane>
      <el-tab-pane label="旷野" name="plain" class="pane" v-if="userStore.soulType > 3"></el-tab-pane>
    </el-tabs>
    <div class="in-room border-wrap">
      <div v-show="!roomStore.id">
        <el-button type="success" plain @click="handleCreateRoom">创建房间</el-button>
        <el-button type="success" plain @click="handleJoinRoomById">房号加入</el-button>
      </div>
      <div v-show="roomStore.id" style="font-size: 14px">
        <p class="room-info">
          <span>
            <span>发布房间：</span>
            <el-switch v-model="roomStore.isRelease" size="small" :before-change="roomStore.releaseRoom" />
          </span>
          <span>
            <span style="margin-right: 5px; user-select: text">房间号：{{ roomStore.id }}</span>
            <el-button size="small" type="success" plain @click="copyText(roomStore.id)">复制</el-button>
          </span>
        </p>
        <p class="room-info">
          <span>
            <span>自动开始：</span>
            <el-switch v-model="roomStore.isAutoStart" size="small" :before-change="roomStore.toggleAutoStart" />
          </span>

          <span style="display: flex; line-height: 25px" v-if="roomStore.closeTime">
            <span>关闭计时：</span>
            <el-countdown format="mm:ss" :value="autoCloseTime" value-style="font-size: 14px" />
          </span>
        </p>
        <p class="room-info affix">
          <span
            >房间类型：<span :class="roomTypeNameMap[roomStore.type || '']?.classes">{{ roomTypeNameMap[roomStore.type || '']?.name }}</span></span
          >
          <span
            >当前怪物：
            <NestMonsterDetail :monster="monster" v-if="roomStore.type === 'nest'" />
            <NestMonsterDetail :monster="monster" v-if="roomStore.type === 'plain'" />
            <RuneRuinMonsterDetail :monster="monster" v-if="roomStore.type === 'runeRuin'" />
            <CrackMonsterDetail :monster="monster" v-if="roomStore.type === 'crack'" />
            <RelicRuinMonsterDetail :monster="monster" v-if="roomStore.type === 'relicRuin'" />
            <CommonMonsterDetail :monster="monster" icon="🪔" v-if="roomStore.type === 'skillCorridor'">
              <template #pre>
                <p v-if="monster">
                  <span
                    >难度：<span :class="crackRank[monster.rank]?.class">{{ crackRank[monster.rank]?.name }}</span></span
                  >
                </p>
                <p v-if="monster?.skillIdList?.length">掉落技能：{{ getSkillNameText(monster.skillIdList) }}</p>
                <p v-if="monster?.skillEnlightenmentScrollRate">
                  启迪卷轴概率：<span class="mp">{{ monster.skillEnlightenmentScrollRate }}%</span>
                </p>
              </template>
            </CommonMonsterDetail>
            <CommonMonsterDetail :monster="monster" icon="" v-if="roomStore.type === 'drageonMountain'">
              <template #pre>
                <p v-if="monster">
                  <span
                    >难度：<span :class="crackRank[monster.rank]?.class">{{ crackRank[monster.rank]?.name }}</span></span
                  >
                </p>
              </template>
            </CommonMonsterDetail>
          </span>
        </p>
        <div class="user-list">
          <p>
            <span>
              {{ roomStore.isJoin ? '您' : '房主' }}：<span class="nickname" :style="{ background: userStore.nicknameColor }">{{
                userStore.nickname
              }}</span>
            </span>
          </p>
          <p>
            <span>
              {{ roomStore.isJoin ? '房主' : '队员' }}：
              <span class="nickname" v-show="roomStore.partnerInfo?.nickname" :style="{ background: roomStore.partnerInfo?.nicknameColor }">
                {{ roomStore.partnerInfo?.nickname }}
              </span>
            </span>
            <el-button v-show="roomStore.partnerInfo?.nickname" type="primary" size="small" plain @click="handleShowOtherUserInfo">查看</el-button>
          </p>
          <br />
          <div v-show="!roomStore.isJoin">
            <el-button type="success" plain @click="handleStartFight">开始战斗</el-button>
            <el-button type="danger" plain @click="roomStore.closeRoom">关闭房间</el-button>
          </div>
          <div v-show="roomStore.isJoin">
            <el-button type="danger" plain @click="handleQuitRoom">退出房间</el-button>
          </div>
        </div>
      </div>
    </div>
    <div class="out-room border-wrap">
      <h4>{{ roomTypeNameMap[curTab]?.name || '全部' }}房间列表</h4>
      <div style="margin-bottom: 10px">
        <el-button type="primary" size="small" plain @click="getRoomList" :disabled="Boolean(refreshTime)">
          {{ !refreshTime ? '刷新' : `请等待（${refreshTime}s）` }}</el-button
        >
      </div>
      <div class="room-list">
        <div class="item border-wrap" v-for="item of roomList">
          <div class="left" style="flex-shrink: unset">
            <p>
              <span style="margin-right: 5px">{{ item.name }}</span>
              <span style="font-size: 12px" class="un-equipped">#{{ item.id }}</span>
            </p>
            <p>
              房间类型：<span :class="roomTypeNameMap[item.type || '']?.classes">{{ roomTypeNameMap[item.type]?.name }}</span>
            </p>
            <div v-show="item.type === 'nest'">
              <p>当前怪物：<NestMonsterDetail :monster="item.monster" /></p>
            </div>
            <div v-show="item.type === 'plain'">
              <p>当前怪物：<NestMonsterDetail :monster="item.monster" /></p>
            </div>
            <div v-show="item.type === 'runeRuin'">
              <p>
                怪物：<RuneRuinMonsterDetail :monster="item.monster" />
                <span style="margin-left: 5px">
                  光泽：<span :class="runeRuinRank[item.monster.rank]?.class">{{ runeRuinRank[item.monster.rank]?.name }}</span>
                </span>
              </p>
              <p>
                <span>
                  倾向：<span :class="soulClass[item.monster.soulType]">{{ RuneRoleText[item.monster.soulType] }}</span>
                </span>
                <span style="margin-left: 10px">
                  赏金：<span class="gold">{{ formatNumberWithCommas(item.monster.gold) }}</span>
                </span>
              </p>
            </div>
            <div v-show="item.type === 'crack'">
              <p>怪物：<CrackMonsterDetail :monster="item.monster" /></p>
              <p>
                <span>
                  报酬：<span class="diamond">{{ formatNumberWithCommas(item.monster.diamond) }}</span>
                </span>
                <span>
                  难度：<span :class="crackRank[item.monster.rank]?.class">{{ crackRank[item.monster.rank]?.name }}</span>
                </span>
              </p>
            </div>
            <div v-show="item.type === 'relicRuin'">
              <p>怪物：<RelicRuinMonsterDetail :monster="item.monster" /></p>
              <p>
                难度：<span :class="crackRank[item.monster.rank]?.class">{{ crackRank[item.monster.rank]?.name }}</span>
              </p>
              <p>
                <span> 圣物线索：<span v-html="explainRelicIdList(item.monster.relicIdList)"></span> </span>
              </p>
            </div>
            <div v-show="item.type === 'skillCorridor'">
              <p>
                怪物：
                <CommonMonsterDetail :monster="item.monster" icon="📘" />
              </p>
              <p>
                难度：<span :class="crackRank[item.monster.rank]?.class">{{ crackRank[item.monster.rank]?.name }}</span>
              </p>
              <p v-if="item.monster?.skillIdList?.length">掉落技能：{{ getSkillNameText(item.monster.skillIdList) }}</p>
              <p v-if="item.monster?.skillEnlightenmentScrollRate">
                启迪卷轴概率：<span class="mp">{{ item.monster.skillEnlightenmentScrollRate }}%</span>
              </p>
            </div>
            <div v-show="item.type === 'drageonMountain'">
              <p>
                怪物：<CommonMonsterDetail :monster="item.monster" icon="">
                  <template #pre>
                    <p>
                      难度：<span :class="crackRank[item.monster.rank]?.class">{{ crackRank[item.monster.rank]?.name }}</span>
                    </p>
                  </template>
                </CommonMonsterDetail>
              </p>
              <p>
                难度：<span :class="crackRank[item.monster.rank]?.class">{{ crackRank[item.monster.rank]?.name }}</span>
              </p>
            </div>
          </div>
          <div class="right">
            <el-button type="primary" plain @click="handleJoinRoom(item.id, item.type)" :disabled="Boolean(roomStore.id)">加入</el-button>
          </div>
        </div>
      </div>
    </div>
    <CommonBtn type="success" style="width: 100%" @done="emits('back')">返回</CommonBtn>

    <el-dialog v-model="showOtherUserInfo" width="100%" style="max-width: 500px; padding: 16px 0">
      <template v-if="otherUserInfo">
        <OtherUserInfo :userInfo="otherUserInfo" />
      </template>
      <template #footer>
        <p style="text-align: center">
          <el-button plain @click="showOtherUserInfo = false" size="small">关闭</el-button>
        </p>
      </template>
    </el-dialog>

    <FooterBar />
  </div>
</template>

<style lang="scss" scoped>
.team-fight-room {
  padding-top: 0;
  position: absolute;
  height: 100%;
  width: 100%;
  top: 0;
  left: 0;
  background-color: var(--backColor);
  z-index: 200;
  display: flex;
  flex-flow: column nowrap;
  .border-wrap {
    h4 {
      display: block;
      font-size: 16px;
      text-align: center;
    }
  }
  .in-room {
    p {
      margin: 6px 0;
    }
    .room-info {
      width: 100%;
      display: flex;
      align-items: center;
      justify-content: space-between;
      span {
        display: flex;
        align-items: center;
      }
    }
    .user-list {
      p {
        display: flex;
        align-items: center;
        button {
          margin-left: 10px;
        }
      }
    }
  }
  .out-room {
    flex: 1;
    display: flex;
    flex-flow: column nowrap;
    overflow: hidden;
    .room-list {
      flex: 1;
      width: 100%;
      overflow: auto;
      .item {
        padding: 5px;
        display: flex;
        align-items: center;
        justify-content: space-between;
        .right {
          flex-shrink: 0;
        }
      }
    }
  }
  .nickname {
    position: relative;
    &::before {
      content: '';
      position: absolute;
      top: 0px;
      left: -5px;
      right: -5px;
      bottom: 0px;
      background-color: #000; /* 阴影颜色 */
      filter: blur(10px);
      z-index: -1; /* 放在元素下方 */
      border-radius: 10px;
    }
  }
}
</style>
